// === IMPORTS ===
import React from 'react';
import { FloorMaterialOption } from '../types';
import { resolveColorToHex } from '../utils/floorColorUtils';
import { Check } from 'lucide-react';
import '../styles/twin-icons-shared.css';
import './FloorTexturePreviewBox.css';

interface FloorTexturePreviewBoxProps {
  material: FloorMaterialOption;
  isSelected?: boolean;
  className?: string;
  heightClass?: string;
  showBadge?: boolean;
  square?: boolean;
}

// === COMPONENTE: VISTA PREVIA DE TEXTURA DE PISO ===
export const FloorTexturePreviewBox: React.FC<FloorTexturePreviewBoxProps> = ({
  material,
  isSelected = false,
  className = '',
  heightClass,
  showBadge = true,
  square = true,
}) => {
  const primaryHex = resolveColorToHex(material.previewColor);

  return (
    <div
      className={`twin-floor-preview ${square ? 'twin-floor-preview--square' : 'twin-floor-preview--custom-height'} ${
        isSelected ? 'twin-floor-preview--selected' : ''
      } ${className}`}
    >
      {/* === VISTA PREVIA: IMAGEN PERSONALIZADA O COLOR/PATRÓN === */}
      {material.customImage ? (
        <div className="twin-floor-preview-image-wrap">
          <img
            src={material.customImage}
            alt={material.name || 'Tile'}
            className="twin-floor-preview-image"
          />
        </div>
      ) : (
        renderSingleTileSwatch(material, primaryHex)
      )}

      {/* === BISEL INTERIOR === */}
      <div className="twin-floor-preview-bevel" />

      {/* === MARCA DE SELECCIÓN ACTIVA === */}
      {isSelected && (
        <div className="twin-floor-preview-check">
          <Check className="twin-icon-xs" />
        </div>
      )}

      {/* === ETIQUETA: IMAGEN VS COLOR === */}
      {showBadge && (
        <div className="twin-floor-preview-badge">
          {material.customImage ? (
            <span>IMG</span>
          ) : (
            <>
              <span
                className="twin-floor-preview-badge-dot"
                style={{ backgroundColor: primaryHex }}
              />
              <span className="twin-floor-preview-badge-label">Tile</span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Renderiza las muestras de textura procedurales para cada material predefinido.
 * Los colores de superposición aquí son parte de la identidad del material físico,
 * no del tema visual de la app — se mantienen fijos independientemente del tema activo.
 */
function renderSingleTileSwatch(
  material: FloorMaterialOption,
  primaryHex: string
) {
  if (material.id === 'wood_parquet') {
    return (
      <div className="twin-swatch twin-swatch--wood" style={{ backgroundColor: primaryHex || '#854d0e' }}>
        <div className="twin-swatch-wood-grain" />
        <div className="twin-swatch-wood-grain" />
        <div className="twin-swatch-wood-grain" />
        <div className="twin-swatch-wood-overlay" />
      </div>
    );
  }

  if (material.id === 'blue_checkered') {
    return (
      <div className="twin-swatch" style={{ backgroundColor: primaryHex || '#0284c7' }}>
        <div className="twin-swatch-checkered-glint" />
        <div className="twin-swatch-glass-overlay" />
      </div>
    );
  }

  if (material.id === 'grey_carpet') {
    return (
      <div
        className="twin-swatch"
        style={{
          backgroundColor: primaryHex || '#475569',
          backgroundImage: 'radial-gradient(#64748b 1.5px, transparent 1.5px)',
          backgroundSize: '4px 4px',
        }}
      >
        <div className="twin-swatch-carpet-overlay" />
      </div>
    );
  }

  if (material.id === 'terracotta') {
    return (
      <div className="twin-swatch" style={{ backgroundColor: primaryHex || '#c2410c' }}>
        <div className="twin-swatch-terracotta-overlay" />
      </div>
    );
  }

  if (material.id === 'bathroom_tile') {
    return (
      <div className="twin-swatch" style={{ backgroundColor: primaryHex || '#0891b2' }}>
        <div className="twin-swatch-tile-glint" />
        <div className="twin-swatch-tile-overlay" />
      </div>
    );
  }

  if (material.id === 'marble_luxury') {
    return (
      <div className="twin-swatch" style={{ backgroundColor: primaryHex || '#f8fafc' }}>
        <div className="twin-swatch-marble-overlay-1" />
        <div className="twin-swatch-marble-overlay-2" />
      </div>
    );
  }

  if (material.id === 'cyber_deck') {
    return (
      <div className="twin-swatch" style={{ backgroundColor: primaryHex || '#1e1b4b' }}>
        <div className="twin-swatch-cyber-dot" />
        <div className="twin-swatch-cyber-overlay" />
      </div>
    );
  }

  // === MUESTRA SÓLIDA POR DEFECTO (colores personalizados) ===
  return (
    <div className="twin-swatch twin-swatch--solid" style={{ backgroundColor: primaryHex }}>
      <div className="twin-swatch-solid-overlay" />
    </div>
  );
}